import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, tap } from 'rxjs';

export interface LogoInfo {
  url: string;
  width: number;
  height: number;
  aspectRatio: number;
  cssClass: string;
  isValid: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LogoUtilityService {
  private readonly http = inject(HttpClient);
  
  // Cache for logo information
  private readonly logoCache = new Map<string, LogoInfo>();
  
  /**
   * Analyze logo dimensions and return appropriate CSS class
   */
  analyzeLogo(url: string): Observable<LogoInfo> {
    // Check cache first
    if (this.logoCache.has(url)) {
      return of(this.logoCache.get(url)!);
    }
    
    return new Observable(observer => {
      const img = new Image();
      
      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const aspectRatio = width / height;
        
        const logoInfo: LogoInfo = {
          url,
          width,
          height,
          aspectRatio,
          cssClass: this.getCssClass(aspectRatio, width, height),
          isValid: true
        };
        
        // Cache the result
        this.logoCache.set(url, logoInfo);
        observer.next(logoInfo);
        observer.complete();
      };
      
      img.onerror = () => {
        const logoInfo: LogoInfo = {
          url,
          width: 0,
          height: 0,
          aspectRatio: 1,
          cssClass: 'logo-square',
          isValid: false
        };
        
        this.logoCache.set(url, logoInfo);
        observer.next(logoInfo);
        observer.complete();
      };
      
      img.src = url;
    });
  }
  
  /**
   * Determine CSS class based on logo dimensions
   */
  private getCssClass(aspectRatio: number, width: number, height: number): string {
    // Very wide logos (banners)
    if (aspectRatio >= 3) {
      return 'logo-wide';
    }
    
    // Wide logos (rectangular)
    if (aspectRatio >= 1.5) {
      return 'logo-rectangle';
    }
    
    // Tall logos
    if (aspectRatio <= 0.7) {
      return 'logo-tall';
    }
    
    // Square-ish logos
    return 'logo-square';
  }
  
  /**
   * Get logo info with fallback
   */
  getLogoInfo(url: string): Observable<LogoInfo> {
    if (!url || url === 'assets/my_fleetman_logo.svg') {
      return of({
        url: 'assets/my_fleetman_logo.svg',
        width: 100,
        height: 100,
        aspectRatio: 1,
        cssClass: 'logo-square',
        isValid: true
      });
    }
    
    return this.analyzeLogo(url);
  }
  
  /**
   * Clear logo cache
   */
  clearCache(): void {
    this.logoCache.clear();
  }
  
  /**
   * Remove specific logo from cache
   */
  removeFromCache(url: string): void {
    this.logoCache.delete(url);
  }
} 